"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, MapPin, Search, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, Target, Compass, ShieldCheck, Building2, ExternalLink } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
const yorkKeywords = [{
  id: "1",
  keyword: "SEO consultant York",
  volume: "880/mo",
  intent: "High Commercial",
  difficulty: "Medium",
  opportunity: "Featured snippets & local pack placement"
}, {
  id: "2",
  keyword: "local SEO services York",
  volume: "520/mo",
  intent: "Transactional",
  difficulty: "Low-Med",
  opportunity: "Google Business Profile & localized hub content"
}, {
  id: "3",
  keyword: "B2B marketing consultancy York",
  volume: "340/mo",
  intent: "Commercial",
  difficulty: "Medium",
  opportunity: "Direct technical consultation positioning"
}, {
  id: "4",
  keyword: "ecommerce SEO agency York",
  volume: "410/mo",
  intent: "Commercial",
  difficulty: "High",
  opportunity: "Audit-led conversion & schema optimization"
}];
const yorkSectorAnalysis = [{
  id: "sec-1",
  sector: "Professional & Financial Services",
  density: "High Local Competition",
  challenge: "Generic agency retainers with zero technical depth.",
  solution: "Direct consultant access, structured entity schema, authority outreach in North Yorkshire."
}, {
  id: "sec-2",
  sector: "Tourism, Heritage & Hospitality",
  density: "Very High Search Volume",
  challenge: "High seasonal query volatility and aggregator dominance.",
  solution: "Hyper-targeted intent clustering, maps optimization, and high-speed mobile UX."
}, {
  id: "sec-3",
  sector: "Tech, Digital & Education",
  density: "Moderate / Growing",
  challenge: "Broad non-converting national traffic keywords.",
  solution: "Bottom-of-funnel keyword mapping and modern AI search optimization (GEO)."
}];
const neighboringCities = [{
  id: "city-1",
  name: "Hull",
  path: "/LocalSeoConsultantHullPage",
  distance: "38 miles",
  focus: "Port Logistics & Maritime SEO"
}, {
  id: "city-2",
  name: "Doncaster",
  path: "/LocalSeoConsultantDoncasterPage",
  distance: "34 miles",
  focus: "Transport & Regional B2B Search"
}, {
  id: "city-3",
  name: "Wakefield",
  path: "/LocalSeoConsultantWakefieldPage",
  distance: "28 miles",
  focus: "Manufacturing & Commercial Local SEO"
}, {
  id: "city-4",
  name: "Lincoln",
  path: "/LocalSeoConsultantLincolnPage",
  distance: "52 miles",
  focus: "Agritech & Heritage Tourism Search"
}, {
  id: "city-5",
  name: "Sunderland",
  path: "/LocalSeoConsultantSunderlandPage",
  distance: "68 miles",
  focus: "Advanced Engineering & Tech SEO"
}, {
  id: "city-6",
  name: "Carlisle",
  path: "/LocalSeoConsultantCarlislePage",
  distance: "85 miles",
  focus: "Regional Retail & Distribution Search"
}];
export default function YorkIntelligence() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localmarketintelligenceyork" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            <MapPin className="size-3.5 mr-1 text-primary" /> York Local Market Intelligence
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default">
            York Search Trends &amp; Organic Growth Opportunities
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            As an independent SEO consultant, I deliver bespoke market analysis for businesses in York and North Yorkshire. 
            Discover how local search volume, competitor gaps, and targeted search strategies can fuel sustainable organic revenue.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <motion.div variants={fadeInVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }}>
            <Card className="h-full border border-border shadow-xs hover:border-primary/40 transition-[border-color]">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                  <TrendingUp className="size-5" />
                </div>
                <CardTitle className="text-xl font-bold font-default">Local Search Volume</CardTitle>
                <CardDescription>
                  High-intent commercial queries across York commercial sectors.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-extrabold text-primary mb-1">14,200+</div>
                <p className="text-sm text-muted-foreground">
                  Monthly searches in York for local professional services, B2B solutions, and high-value ecommerce queries.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={fadeInVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }}>
            <Card className="h-full border border-border shadow-xs hover:border-primary/40 transition-[border-color]">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 rounded-lg bg-accent/20 text-accent-foreground flex items-center justify-center mb-3">
                  <Target className="size-5 text-accent" />
                </div>
                <CardTitle className="text-xl font-bold font-default">Competitor Gaps</CardTitle>
                <CardDescription>
                  Unlocking opportunities overlooked by traditional agencies.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-extrabold text-foreground mb-1">64%</div>
                <p className="text-sm text-muted-foreground">
                  Of York business websites lack structured entity data, localized schema markup, and robust technical optimization.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={fadeInVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }}>
            <Card className="h-full border border-border shadow-xs hover:border-primary/40 transition-[border-color]">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                  <Compass className="size-5" />
                </div>
                <CardTitle className="text-xl font-bold font-default">Direct Access Advantage</CardTitle>
                <CardDescription>
                  Working directly with Yassine Essaadi.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-extrabold text-primary mb-1">100%</div>
                <p className="text-sm text-muted-foreground">
                  Direct consultant execution. No account managers, no generic junior delegates, and complete strategy transparency.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 sm:p-8 shadow-xs mb-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h3 className="text-xl font-bold text-card-foreground flex items-center gap-2 font-default">
                <Search className="size-5 text-primary" /> Key York Search Keywords &amp; Opportunity Matrix
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Verified search metrics and strategy focus areas for businesses targeting the York regional market.
              </p>
            </div>
            <Badge variant="outline" className="w-fit text-xs px-3 py-1">
              Updated Live Analysis
            </Badge>
          </div>

          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="font-semibold text-foreground">Target Search Query</TableHead>
                <TableHead className="font-semibold text-foreground">Search Volume</TableHead>
                <TableHead className="font-semibold text-foreground">Search Intent</TableHead>
                <TableHead className="font-semibold text-foreground">Competition</TableHead>
                <TableHead className="font-semibold text-foreground">Strategic Opportunity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {yorkKeywords.map((item, i) => <TableRow key={item.id} data-index={i} className="border-border/60 hover:bg-muted/40">
                  <TableCell className="font-medium text-foreground">{item.keyword}</TableCell>
                  <TableCell className="text-muted-foreground">{item.volume}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-xs">
                      {item.intent}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{item.difficulty}</TableCell>
                  <TableCell className="text-xs text-foreground font-medium">{item.opportunity}</TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </div>

        <div className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-default">
              Sector-Specific Competition Analysis in York
            </h3>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base">
              Every industry in North Yorkshire faces distinct search challenges. Here is how my tailored SEO approach solves them.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {yorkSectorAnalysis.map((sector, i) => <div key={sector.id} data-index={i} className="bg-card border border-border rounded-xl p-6 shadow-xs flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-semibold text-primary uppercase tracking-wider">Sector Focus</span>
                    <Badge variant="outline" className="text-xs">
                      {sector.density}
                    </Badge>
                  </div>
                  <h4 className="text-lg font-bold text-foreground mb-2 font-default">{sector.sector}</h4>
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-muted-foreground uppercase">Current Gap:</span>
                    <p className="text-sm text-foreground mt-1">{sector.challenge}</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <span className="text-xs font-semibold text-primary uppercase">Consultant Solution:</span>
                  <p className="text-sm text-muted-foreground mt-1 flex items-start gap-1.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{sector.solution}</span>
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        <div className="bg-muted/40 border border-border rounded-2xl p-8 md:p-10">
          <div className="max-w-3xl mb-8">
            <Badge variant="default" className="mb-3 bg-primary text-primary-foreground">
              Geographic SEO Coverage
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-default">
              Neighboring Regional &amp; City SEO Expertise
            </h3>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base">
              Looking for tailored local search consultancy in surrounding areas? I support businesses across Yorkshire 
              and the North East with targeted multi-location search strategies.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {neighboringCities.map((city, i) => <div key={city.id} data-index={i}>
                <Link to={city.path} className="group block bg-background border border-border rounded-xl p-4 hover:border-primary/50 transition-[border-color,box-shadow] shadow-2xs hover:shadow-md">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-foreground group-hover:text-primary transition-colors flex items-center gap-1.5 font-default">
                      <Building2 className="size-4 text-primary" /> {city.name}
                    </span>
                    <span className="text-xs text-muted-foreground">{city.distance}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">{city.focus}</p>
                  <div className="flex items-center text-xs font-medium text-primary gap-1">
                    <span>Explore Local Strategy</span>
                    <ArrowRight className="size-3 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              </div>)}
          </div>

          <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-foreground">
              <ShieldCheck className="size-5 text-primary shrink-0" />
              <span>Direct execution by Yassine Essaadi for York &amp; national businesses.</span>
            </div>
            <div className="flex gap-3">
              <Button asChild variant="default">
                <Link to="/LocalSEOPage">
                  View Main Local SEO Service <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/ContactPage">
                  Book Free Consultation
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}